import { useState } from 'react';
import { EyeOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface HiddenSectionBannerProps {
  /** Friendly section label (from SECTION_METADATA). */
  label: string;
  /**
   * When true, the section is hidden by the Layer-1 hard gate (feature flag off).
   * A per-project override cannot beat the hard gate, so the "Show section"
   * button is suppressed — only the explanatory text is shown.
   */
  hardGated: boolean;
  /** Runs the show-semantics for the targeted section (and expands it). */
  onShow: () => void;
}

/**
 * Inline banner shown when a `?section=` deep link targets an add-in that
 * resolved HIDDEN. Explains why the section isn't visible and (for soft-hidden
 * sections) offers a one-click "Show section" affordance. Dismissible.
 */
export function HiddenSectionBanner({ label, hardGated, onShow }: HiddenSectionBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
      data-testid="hidden-section-banner"
    >
      <div className="flex items-center gap-2 text-sm text-amber-900">
        <EyeOff className="h-4 w-4 flex-shrink-0 text-amber-600" />
        <span>The {label} section is hidden on this project.</span>
      </div>
      <div className="flex items-center gap-1">
        {!hardGated && (
          <Button
            variant="outline"
            size="sm"
            onClick={onShow}
            data-testid="hidden-section-banner-show"
          >
            Show section
          </Button>
        )}
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded hover:bg-amber-100 transition-colors text-amber-700"
          aria-label="Dismiss"
          data-testid="hidden-section-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
